/* Progressive enhancement for course floor tabs and the native image dialog. */
(() => {
  if (customElements.get('atlas-event-course')) return;
  class AtlasEventCourse extends HTMLElement {
    connectedCallback() {
      if (this.controller || this.startupObserver) return;
      const initialize = () => {
        if (!this.isConnected || !this.querySelector('[data-aec-zoom-image]')) return;
        this.startupObserver?.disconnect(); this.startupObserver = null;
        this.initialize();
      };
      if (this.querySelector('[data-aec-zoom-image]')) initialize();
      else {
        this.startupObserver = new MutationObserver(initialize);
        this.startupObserver.observe(this, {childList:true,subtree:true});
      }
    }
    initialize() {
      if (this.controller) return;
      this.controller = new AbortController();
      this.tabs = [...this.querySelectorAll('[data-aec-tab]')];
      this.panels = [...this.querySelectorAll('[data-aec-panel]')];
      this.toolbar = this.querySelector('[data-aec-toolbar]');
      this.enlarge = this.querySelector('[data-aec-enlarge]');
      this.dialog = this.querySelector('[data-aec-dialog]');
      this.viewer = this.querySelector('[data-aec-zoom-image]');
      this.overview = this.dataset.viewMode !== 'tabs';
      this.supportsDialog = typeof this.dialog?.showModal === 'function';
      this.toolbar.hidden = false;
      this.panels.forEach(panel => {
        if (this.overview) return;
        panel.setAttribute('role','tabpanel');
        panel.setAttribute('aria-labelledby',this.tabs.find(tab => tab.dataset.aecTab === panel.dataset.aecPanel)?.id || '');
        panel.tabIndex = 0;
      });
      this.listen(this,'click',event => {
        const tab = event.target.closest('[data-aec-tab]');
        if (tab && this.contains(tab)) this.select(tab.dataset.aecTab);
        if (event.target.closest('[data-aec-enlarge]')) this.openViewer();
        if (event.target.closest('[data-aec-close]')) this.dialog.close();
        const zoom = event.target.closest('[data-aec-zoom]');
        if (zoom) this.setZoom(zoom.dataset.aecZoom === 'reset' ? 1 : this.zoom + (zoom.dataset.aecZoom === 'in' ? .5 : -.5));
      });
      this.listen(this,'keydown',event => {
        const tab = event.target.closest('[data-aec-tab]');
        if (!tab || !['ArrowLeft','ArrowRight','Home','End'].includes(event.key)) return;
        event.preventDefault();
        let index = this.tabs.indexOf(tab);
        if (event.key === 'Home') index = 0;
        else if (event.key === 'End') index = this.tabs.length-1;
        else index = (index + (event.key === 'ArrowRight' ? 1 : -1) + this.tabs.length) % this.tabs.length;
        this.select(this.tabs[index].dataset.aecTab);
        this.tabs[index].focus();
      });
      this.listen(this.dialog,'close',() => {
        this.viewer.replaceChildren();
        if (this.isConnected && this.opener?.isConnected) this.opener.focus({preventScroll:true});
      });
      this.listen(this.dialog,'click',event => {
        if (event.target !== this.dialog) return;
        const rect = this.dialog.getBoundingClientRect();
        if (event.clientX < rect.left || event.clientX > rect.right || event.clientY < rect.top || event.clientY > rect.bottom) this.dialog.close();
      });
      this.listen(document,'shopify:block:select',event => {
        if (event.detail?.blockId === this.dataset.blockId || this.contains(event.target)) this.querySelector('.aec__disclosure').open = true;
      });
      const firstMap = this.panels.find(panel => panel.querySelector('.aec__map img')) || this.panels[0];
      this.select(firstMap.dataset.aecPanel);
      this.setAttribute('data-aec-ready','');
    }
    listen(target,type,handler) {target?.addEventListener(type,handler,{signal:this.controller.signal});}
    select(id) {
      if (!this.panels.some(panel => panel.dataset.aecPanel === id)) return;
      this.active = id;
      this.tabs.forEach(tab => {
        const selected = tab.dataset.aecTab === id;
        tab.setAttribute('aria-selected',String(selected));
        tab.tabIndex = selected ? 0 : -1;
      });
      this.panels.forEach(panel => panel.hidden = !this.overview && panel.dataset.aecPanel !== id);
      this.enlarge.hidden = !this.supportsDialog || !this.panel()?.querySelector('.aec__map img');
    }
    panel() {return this.panels.find(panel => panel.dataset.aecPanel === this.active);}
    openViewer() {
      const selected = this.overview ? this.panels : [this.panel()];
      const sources = selected.filter(panel => panel?.querySelector('.aec__diagram img'));
      if (!sources.length || !this.supportsDialog || this.dialog.open) return;
      this.opener = document.activeElement;
      this.viewer.replaceChildren(...sources.map(panel => {
        const figure = document.createElement('figure');
        figure.className = 'aec__zoom-plan';
        const label = document.createElement('figcaption');
        label.textContent = panel.querySelector('.aec__map-title')?.textContent || panel.querySelector('h3')?.textContent || '';
        const diagram = panel.querySelector('.aec__diagram').cloneNode(true);
        diagram.querySelectorAll('img').forEach(image => {image.loading = 'eager';image.removeAttribute('id');image.removeAttribute('sizes');});
        figure.append(label,diagram);
        return figure;
      }));
      this.querySelector('[data-aec-floor-label]').textContent = sources.map(panel => panel.querySelector('.aec__map-title')?.textContent || '').filter(Boolean).join(' · ');
      this.setZoom(1);
      this.dialog.showModal();
      this.querySelector('[data-aec-close]').focus();
    }
    setZoom(value) {
      this.zoom = Math.max(1,Math.min(3,value));
      this.viewer.style.width = this.zoom * 100 + '%';
      this.querySelector('[data-aec-zoom-value]').textContent = Math.round(this.zoom*100) + '%';
      this.querySelector('[data-aec-zoom="out"]').disabled = this.zoom <= 1;
      this.querySelector('[data-aec-zoom="in"]').disabled = this.zoom >= 3;
      if (this.zoom === 1) {const viewport = this.querySelector('.aec__viewport');viewport.scrollLeft = 0;viewport.scrollTop = 0;}
    }
    disconnectedCallback() {
      this.startupObserver?.disconnect(); this.startupObserver = null;
      this.controller?.abort(); this.controller = null;
      if (this.dialog?.open) this.dialog.close();
      this.viewer?.replaceChildren();
      this.tabs?.forEach(tab => tab.tabIndex = -1);
      this.panels?.forEach(panel => {panel.hidden = false;panel.removeAttribute('role');panel.removeAttribute('aria-labelledby');panel.removeAttribute('tabindex');});
      if (this.toolbar) this.toolbar.hidden = true;
      this.removeAttribute('data-aec-ready');
    }
  }
  customElements.define('atlas-event-course',AtlasEventCourse);
})();
